import React, { PropsWithChildren, createContext, useState } from "react";
import { DocumentType } from "../__generated__/graphql";
import { navigationRef } from "../navigations/navigationRef";

// Documents come from various GraphQL operations with slightly different selections. The display
// flow only needs these fields; extras (asset, etc.) are ignored, so keep them optional here.
export type DisplayableDocument = {
    id: string;
    type: DocumentType;
    title: string;
    documentUrl?: string | null;
    inlineContent?: string | null;
};

// The live edit session, read by the DocumentEditor modal SCREEN (components/document/
// DocumentEditorScreen.tsx) through context — the callbacks aren't serialisable, so they can't travel
// as navigation params.
export type DocumentEditorSession = {
    document?: DisplayableDocument;
    mimeType?: string;
    onSave?: (document: DisplayableDocument) => void | Promise<void>;
    onRemove?: (document: DisplayableDocument) => void;
};

interface DocumentContextInterface {
    editDocument: (params: {
        document: DisplayableDocument;
        onSave?: (document: DisplayableDocument) => void | Promise<void>;
        onDelete?: (document: DisplayableDocument) => void;
    }) => void;
    showDocument: (params: { document: DisplayableDocument; knownMimeType?: string }) => void;
    editorSession: DocumentEditorSession;
    closeEditor: () => void;
    clearEditorSession: () => void;
}

export const DocumentContext = createContext<DocumentContextInterface>({
    editDocument: () => {},
    showDocument: () => {},
    editorSession: {},
    closeEditor: () => {},
    clearEditorSession: () => {},
});

type DocumentManagerProps = PropsWithChildren;

const DocumentManager = ({ children }: DocumentManagerProps) => {
    const [editedDocument, setEditedDocument] = useState<DisplayableDocument>();
    const [knownMimType, setKnownMimeType] = useState<string | undefined>(undefined);
    const [onSaveCallback, setOnSaveCallback] = useState<
        ((document: DisplayableDocument) => void | Promise<void>) | undefined
    >(undefined);
    const [onDeleteCallback, setOnDeleteCallback] = useState<
        ((document: DisplayableDocument) => void) | undefined
    >(undefined);

    // Open/close the DocumentEditor modal SCREEN — a real navigation modal, presented the same way on
    // all platforms (an animated centered dialog on web, a native modal on iOS/Android), so it stacks
    // above other modals like CreateMissionStack.
    const openEditor = React.useCallback(() => {
        if (navigationRef.isReady()) {
            navigationRef.navigate("DocumentEditor");
        }
    }, []);

    const closeEditor = React.useCallback(() => {
        if (navigationRef.isReady() && navigationRef.canGoBack()) {
            navigationRef.goBack();
        }
    }, []);

    // Cleared when the screen unmounts — covers every dismissal path (close button, save, and the
    // native swipe / hardware-back dismiss), so the next open always starts from a clean session.
    const clearEditorSession = React.useCallback(() => {
        setEditedDocument(undefined);
        setKnownMimeType(undefined);
        setOnSaveCallback(undefined);
        setOnDeleteCallback(undefined);
    }, []);

    const saveDocument = React.useCallback(
        async (document: DisplayableDocument) => {
            if (onSaveCallback) {
                await onSaveCallback(document);
            }
            closeEditor();
        },
        [onSaveCallback, closeEditor],
    );

    const deleteDocument = React.useCallback(
        (document: DisplayableDocument) => {
            onDeleteCallback?.(document);
            closeEditor();
        },
        [onDeleteCallback, closeEditor],
    );

    const editDocument = React.useCallback(
        (params: {
            document: DisplayableDocument;
            knownMimeType?: string;
            onSave?: (document: DisplayableDocument) => void | Promise<void>;
            onDelete?: (document: DisplayableDocument) => void;
        }) => {
            setOnSaveCallback(() => params.onSave);
            setOnDeleteCallback(() => params.onDelete);
            setKnownMimeType(params.knownMimeType);
            setEditedDocument(params.document);
            openEditor();
        },
        [openEditor],
    );

    const showDocument = React.useCallback(
        (params: { document: DisplayableDocument; knownMimeType?: string }) => {
            setOnSaveCallback(undefined);
            setOnDeleteCallback(undefined);
            setKnownMimeType(params.knownMimeType);
            setEditedDocument(params.document);
            openEditor();
        },
        [openEditor],
    );

    const editorSession = React.useMemo<DocumentEditorSession>(
        () => ({
            document: editedDocument,
            mimeType: knownMimType,
            onSave: onSaveCallback !== undefined ? saveDocument : undefined,
            onRemove: onDeleteCallback !== undefined ? deleteDocument : undefined,
        }),
        [
            editedDocument,
            knownMimType,
            onSaveCallback,
            onDeleteCallback,
            saveDocument,
            deleteDocument,
        ],
    );

    return (
        <DocumentContext.Provider
            value={{ editDocument, showDocument, editorSession, closeEditor, clearEditorSession }}
        >
            {children}
        </DocumentContext.Provider>
    );
};

export default DocumentManager;
